import {clearChatReferral} from '@/lib/chat-referral';
import {
  createContext,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  loginLocal as apiLogin,
  TotpRequiredError,
  logout as apiLogout,
  createCustomer as apiCreateCustomer,
  fetchMe,
  switchCustomer as apiSwitchCustomer,
  type MeResponse,
  type LoginRequest,
  type CustomerCreateRequest,
} from "@/api/auth";
import { onSessionExpired } from "@/api/client";
import { setActiveOrganizationScopeId } from "@/lib/organization-scope";

export interface AuthState {
  user: MeResponse | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (req: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  switchCustomer: (customerId: string) => Promise<void>;
  createCustomer: (req: CustomerCreateRequest) => Promise<void>;
  refreshMe: () => Promise<void>;
}

export const AuthContext = createContext<AuthState>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
  switchCustomer: async () => {},
  createCustomer: async () => {},
  refreshMe: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const commitUser = useCallback((nextUser: MeResponse | null) => {
    if(nextUser)clearChatReferral();
    const nextOrganizationId = nextUser?.currentMembership?.customerId ?? null;
    setActiveOrganizationScopeId(nextOrganizationId);
    setUser(nextUser);
  }, []);

  useEffect(() => {
    setActiveOrganizationScopeId(null);
    fetchMe()
      .then(commitUser)
      .catch(() => commitUser(null))
      .finally(() => setIsLoading(false));
  }, [commitUser]);

  // Listen for 401 session expiry from API client
  useEffect(() => {
    return onSessionExpired(() => {
      commitUser(null);
    });
  }, [commitUser]);

  const login = useCallback(async (req: LoginRequest) => {
    const result = await apiLogin(req);
    if ("totpRequired" in result) throw new TotpRequiredError();
    const me = await fetchMe();
    commitUser(me);
  }, [commitUser]);

  const refreshMe = useCallback(async () => {
    const me = await fetchMe();
    commitUser(me);
  }, [commitUser]);

  const refreshAfterOrganizationScopeMutation = useCallback(
    async (expectedOrganizationId: string) => {
      setIsLoading(true);
      setActiveOrganizationScopeId(null);
      try {
        const me = await fetchMe();
        if (me.currentMembership?.customerId !== expectedOrganizationId) {
          throw new Error("organization_scope_mismatch");
        }
        commitUser(me);
      } catch (cause) {
        commitUser(null);
        throw cause;
      } finally {
        setIsLoading(false);
      }
    },
    [commitUser],
  );

  const switchCustomer = useCallback(
    async (customerId: string) => {
      const switched = await apiSwitchCustomer(customerId);
      if (switched.currentMembership.customerId !== customerId) {
        commitUser(null);
        throw new Error("organization_scope_mismatch");
      }
      await refreshAfterOrganizationScopeMutation(customerId);
    },
    [commitUser, refreshAfterOrganizationScopeMutation],
  );

  const createCustomer = useCallback(
    async (req: CustomerCreateRequest) => {
      const created = await apiCreateCustomer(req);
      await refreshAfterOrganizationScopeMutation(
        created.currentMembership.customerId,
      );
    },
    [refreshAfterOrganizationScopeMutation],
  );

  const logout = useCallback(async () => {
    await apiLogout();
    commitUser(null);
  }, [commitUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        switchCustomer,
        createCustomer,
        refreshMe,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
